import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import CommunityLayout from '../../../components/community/CommunityLayout';
import NoticeCard from '../../../components/common/NoticeCard';
import { getPostList, PostListItem, ApiResponse, PostListResponse } from '../../../services/postApi';
import { getStudyCategoriesList, StudyCategory } from '../../../services/studyCategoryApi';
import { searchStudyPosts, PostSearchItem } from '../../../services/searchApi';

const Study: React.FC = () => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null); // 선택된 카테고리 ID
  const [postList, setPostList] = useState<PostListItem[]>([]);
  const [studyCategories, setStudyCategories] = useState<StudyCategory[]>([]); // 스터디 카테고리 목록
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true); // 카테고리 로딩 상태
  const [page, setPage] = useState(0);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  
  // 검색 관련 상태
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchResults, setSearchResults] = useState<PostSearchItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // 스터디 카테고리 목록 조회
  const fetchStudyCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      console.log('스터디 카테고리 목록 조회 시작');
      
      const response = await getStudyCategoriesList();
      console.log('스터디 카테고리 API 응답:', response);
      
      if (response.code === 200) {
        setStudyCategories(response.data);
        console.log('스터디 카테고리 목록 설정:', response.data);
      }
    } catch (error) {
      console.error('스터디 카테고리 목록 조회 실패:', error);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  // 게시글 목록 조회
  const fetchPosts = useCallback(async (pageNum: number = 0, reset: boolean = false) => {
    console.log('Study fetchPosts 호출됨:', { pageNum, reset, loading, selectedCategory });
    if (loading) return;
    
    setLoading(true);
    try {
      console.log('API 요청 시작 - board: study, studyCategoryId:', selectedCategory);
      const response: ApiResponse<PostListResponse> = await getPostList(
        'study',
        pageNum,
        10,
        undefined, // lectureCategoryId
        undefined, // lectureId
        undefined, // chapter
        selectedCategory || undefined, // studyCategoryId
        undefined // lecturerId
      );

      console.log('Study API 응답:', response);
      if (response.data) {
        const newPosts = response.data.content;
        console.log('받은 게시글 수:', newPosts.length);
        setPostList(reset ? newPosts : prev => [...prev, ...newPosts]);
        setHasMorePosts(!response.data.last);
      }
    } catch (error: any) {
      console.error('게시글 목록 조회 실패:', error);
      if (error.message === 'Unauthorized') {
        navigate('/login');
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, selectedCategory]);

  // 검색 함수
  const performSearch = useCallback(async (keyword: string) => {
    if (!keyword.trim()) {
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchQuery('');
      return;
    }

    setSearchLoading(true);
    setSearchError(null);
    setIsSearchMode(true);
    setSearchQuery(keyword);

    try {
      console.log('스터디 검색 실행:', keyword);
      const results = await searchStudyPosts(keyword);
      console.log('스터디 검색 결과:', results);
      setSearchResults(results);
    } catch (error) {
      console.error('스터디 검색 오류:', error);
      setSearchError('검색 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // 검색 초기화 함수
  const clearSearch = useCallback(() => {
    setSearchValue('');
    setSearchQuery('');
    setIsSearchMode(false);
    setSearchResults([]);
    setSearchError(null);
  }, []);

  // 검색어가 비어있으면 검색 모드 해제
  useEffect(() => {
    if (!searchValue.trim()) {
      clearSearch();
    }
  }, [searchValue, clearSearch]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchStudyCategories(); // 스터디 카테고리 로드
  }, [fetchStudyCategories]); // fetchStudyCategories가 변경될 때만 실행

  useEffect(() => {
    fetchPosts(0, true); // 게시글 로드
  }, [fetchPosts]); // fetchPosts가 변경될 때만 실행

  // 선택된 카테고리가 변경될 때 게시글 새로고침
  useEffect(() => {
    setPage(0); // 페이지 초기화
    fetchPosts(0, true); // 첫 페이지부터 새로 로드
  }, [selectedCategory, fetchPosts]); // selectedCategory와 fetchPosts 의존성으로

  // 페이지 로드 더보기
  const loadMorePosts = () => {
    if (hasMorePosts && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPosts(nextPage, false);
    }
  };

  // 게시글 클릭 핸들러
  const handlePostClick = (postId: number) => {
    navigate(`/community/post/${postId}`);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const searchTerm = (e.target as HTMLInputElement).value.trim();
      if (searchTerm) {
        console.log(`검색: ${searchTerm}`);
        performSearch(searchTerm);
      } else {
        clearSearch();
      }
    }
  };

  // 공지사항 필터링 (pinned=true인 게시글)
  const pinnedPosts = postList.filter(post => post.pinned);
  const regularPosts = postList.filter(post => !post.pinned);

  return (
    <CommunityLayout hideFooter={true}>
      <Container>
        <MainContainer>
          {/* 페이지 헤더 */}
          <PageHeader>
            <PageTitle>스터디</PageTitle>
          </PageHeader>

          {/* 브레드크럼과 검색바 */}
          <NavigationBar>
            <BreadcrumbContainer>
              <BreadcrumbItem>홈</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>커뮤니티</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>스터디</BreadcrumbItem>
            </BreadcrumbContainer>
            
            <SearchContainer>
              <SearchBox>
                <SearchIcon>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path 
                      d="M14.87 14.87L10.4 10.4M12 6.5C12 9.54 9.54 12 6.5 12C3.46 12 1 9.54 1 6.5C1 3.46 3.46 1 6.5 1C9.54 1 12 3.46 12 6.5Z" 
                      stroke="rgba(55, 56, 60, 0.28)" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </SearchIcon>
                <SearchInput 
                  type="text" 
                  placeholder={searchLoading ? "검색 중..." : "검색어를 입력해주세요."}
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchSubmit}
                  disabled={searchLoading}
                />
              </SearchBox>
            </SearchContainer>
          </NavigationBar>

          {/* 카테고리 필터 */}
          <CategoryFilterSection>
            {/* 카테고리 칩 필터 */}
            <ChipContainer>
              <ChipButton 
                active={selectedCategory === null} 
                onClick={() => setSelectedCategory(null)}
              >
                전체보기
              </ChipButton>
              {categoriesLoading ? (
                <LoadingText>카테고리 로딩 중...</LoadingText>
              ) : (
                studyCategories.map((category) => (
                  <ChipButton 
                    key={category.id}
                    active={selectedCategory === category.id} 
                    onClick={() => setSelectedCategory(category.id)}
                  >
                    {category.name}
                  </ChipButton>
                ))
              )}
            </ChipContainer>
          </CategoryFilterSection>

          {/* 상단고정 공지사항 */}
          {pinnedPosts.map(notice => (
            <NoticeCard 
              key={notice.post_id}
              title={notice.title}
              onClick={() => handlePostClick(notice.post_id)}
            />
          ))}

          {/* 게시글 목록 */}
          <StudyList>
            {/* 검색 로딩 상태 */}
            {searchLoading ? (
              <LoadingContainer>
                <LoadingSpinner />
                <LoadingText>검색 중...</LoadingText>
              </LoadingContainer>
            ) : searchError ? (
              /* 검색 오류 표시 */
              <ErrorContainer>
                <ErrorText>{searchError}</ErrorText>
                <RetryButton onClick={() => performSearch(searchQuery)}>
                  다시 시도
                </RetryButton>
              </ErrorContainer>
            ) : isSearchMode ? (
              /* 검색 결과 표시 */
              searchResults.length > 0 ? (
                searchResults.map((study, index) => (
                  <React.Fragment key={study.id}>
                    <StudyCard onClick={() => handlePostClick(study.id)}>
                      <StudyContent>
                        <StudyHeader>
                          <StudyTitle>{study.title}</StudyTitle>
                          <StudyDescription
                            dangerouslySetInnerHTML={{ __html: study.htmlcontent.replace(/<[^>]*>/g, '') }}
                          />
                        </StudyHeader>
                        
                        <StudyMeta>
                          <StudyCategoryTags>
                            <StudyCategoryText>{study.category}</StudyCategoryText>
                            {study.studyCategoryName && (
                              <>
                                <Divider />
                                <StudyType>{study.studyCategoryName}</StudyType>
                              </>
                            )}
                            <Divider />
                            <StudyRecruitStatus isCompleted={false}>
                              모집중
                            </StudyRecruitStatus>
                          </StudyCategoryTags>
                        </StudyMeta>
                      </StudyContent>
                      
                      <StudyFooter>
                        <StudyAuthorInfo>
                          <AuthorAvatar>
                            <img src={study.writerProfileImageUrl || "/images/default-avatar.png"} alt="작성자" />
                          </AuthorAvatar>
                          <AuthorName>{study.writerName}</AuthorName>
                          <Divider />
                          <StudyDate>{new Date(study.postDate).toLocaleDateString('ko-KR')}</StudyDate>
                        </StudyAuthorInfo>
                        
                        <StudyStats>
                          <StatItem>인원 -/-</StatItem>
                          <Divider />
                          <StatItem>조회수 -</StatItem>
                        </StudyStats>
                      </StudyFooter>
                    </StudyCard>
                    
                    {index < searchResults.length - 1 && <StudyDivider />}
                  </React.Fragment>
                ))
              ) : (
                /* 검색 결과가 없을 때 */
                <EmptySearchContainer>
                  <EmptySearchText>'{searchQuery}' 에 대한 검색 결과가 없습니다.</EmptySearchText>
                  <EmptySearchSubText>다른 검색어로 다시 시도해보세요.</EmptySearchSubText>
                </EmptySearchContainer>
              )
            ) : (
              /* 일반 모드 - 기존 게시글 목록 */
              regularPosts.length > 0 ? (
                regularPosts.map((study, index) => (
                  <React.Fragment key={study.post_id}>
                    <StudyCard onClick={() => handlePostClick(study.post_id)}>
                      <StudyContent>
                        <StudyHeader>
                          <StudyTitle>{study.title}</StudyTitle>
                          <StudyDescription>
                            {study.content.replace(/<[^>]*>/g, '')} {/* HTML 태그 제거 */}
                          </StudyDescription>
                        </StudyHeader>
                        
                        <StudyMeta>
                          <StudyCategoryTags>
                            <StudyCategoryText>{study.post_category}</StudyCategoryText>
                            {study.study_name && (
                              <>
                                <Divider />
                                <StudyType>{study.study_name}</StudyType>
                              </>
                            )}
                            <Divider />
                            <StudyRecruitStatus isCompleted={false}>
                              모집중
                            </StudyRecruitStatus>
                          </StudyCategoryTags>
                        </StudyMeta>
                      </StudyContent>
                      
                      <StudyFooter>
                        <StudyAuthorInfo>
                          <AuthorAvatar>
                            <img src="/images/default-avatar.png" alt="작성자" />
                          </AuthorAvatar>
                          <AuthorName>{study.writer_name}</AuthorName>
                          <Divider />
                          <StudyDate>{new Date(study.created_at).toLocaleDateString('ko-KR')}</StudyDate>
                        </StudyAuthorInfo>
                        
                        <StudyStats>
                          <StatItem>인원 -/-</StatItem>
                          <Divider />
                          <StatItem>조회수 {study.view_cnt}</StatItem>
                        </StudyStats>
                      </StudyFooter>
                    </StudyCard>
                    
                    {index < regularPosts.length - 1 && <StudyDivider />}
                  </React.Fragment>
                ))
              ) : (
                /* 일반 모드에서 게시글이 없을 때 */
                <EmptyContainer>
                  <EmptyText>아직 등록된 스터디가 없습니다.</EmptyText>
                  <EmptySubText>첫 번째 스터디를 등록해보세요!</EmptySubText>
                </EmptyContainer>
              )
            )}
          </StudyList>

          {/* 더보기 버튼 */}
          {/* {hasMorePosts && (
            <LoadMoreContainer>
              <LoadMoreButton onClick={loadMorePosts} disabled={loading}>
                {loading ? '로딩 중...' : '더보기'}
              </LoadMoreButton>
            </LoadMoreContainer>
          )} */}
        </MainContainer>
      </Container>
    </CommunityLayout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 894px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 60px;
`;

const PageTitle = styled.h1`
  color: #171719;
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  margin: 0;
`;

const NavigationBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const BreadcrumbItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const ChevronIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchContainer = styled.div`
  width: 282px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchBox = styled.div`
  width: 100%;
  padding: 8px;
  background: white;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  outline: 1px solid rgba(112, 115, 124, 0.22);
  outline-offset: -1px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const SearchIcon = styled.div`
  width: 16px;
  height: 16px;
  padding: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: rgba(55, 56, 60, 0.28);
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CategoryFilterSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StudyList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StudyCard = styled.div`
  width: 100%;
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: rgba(112, 115, 124, 0.02);
  }
`;

const StudyContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StudyHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StudyTitle = styled.h3`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  margin: 0;
`;

const StudyDescription = styled.p`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 21.99px;
  letter-spacing: 0.20px;
  margin: 0;
  
  /* 3줄 말줄임 */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StudyMeta = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const StudyCategoryTags = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const StudyCategoryText = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const StudyType = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const StudyRecruitStatus = styled.span<{ isCompleted: boolean }>`
  color: ${props => props.isCompleted ? 'rgba(55, 56, 60, 0.41)' : '#448181'};
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const StudyFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StudyAuthorInfo = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const AuthorAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  outline: 1px solid rgba(112, 115, 124, 0.08);
  outline-offset: -1px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorName = styled.span`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const StudyDate = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const StudyStats = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const StatItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(112, 115, 124, 0.22);
`;

const StudyDivider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
`;

const LoadMoreContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 32px;
`;

const LoadMoreButton = styled.button<{ disabled: boolean }>`
  padding: 12px 24px;
  background: ${props => props.disabled ? '#f5f5f5' : '#448181'};
  color: ${props => props.disabled ? '#999' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s ease;

  &:hover {
    background: ${props => props.disabled ? '#f5f5f5' : '#3a6b6b'};
  }
`;

// 검색 관련 스타일 컴포넌트
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #448181;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
`;

const ErrorText = styled.span`
  color: #d32f2f;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  text-align: center;
`;

const RetryButton = styled.button`
  padding: 8px 16px;
  background: #448181;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #3a6b6b;
  }
`;

const EmptySearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 8px;
`;

const EmptySearchText = styled.span`
  color: #171719;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 26px;
  text-align: center;
`;

const EmptySearchSubText = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 8px;
`;

const EmptyText = styled.span`
  color: #171719;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 26px;
  text-align: center;
`;

const EmptySubText = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
`;

const ChipContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const ChipButton = styled.button<{ active: boolean }>`
  position: relative;
  padding: 7px 11px;
  background: ${props => props.active ? '#171719' : 'white'};
  color: ${props => props.active ? '#F7F7F8' : 'rgba(55, 56, 60, 0.61)'};
  border: ${props => props.active ? 'none' : '1px solid rgba(112, 115, 124, 0.16)'};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 15px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  
  &:hover {
    background: ${props => props.active ? '#171719' : 'rgba(112, 115, 124, 0.08)'};
  }
`;

const LoadingText = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 15px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  padding: 7px 11px;
`;

export default Study;
